import { AnalysisStatus } from '../../../common/entities/analysis-status.entity';
import { RiskLevel } from '../../../common/entities/risk-level.entity';
import { AnalysisListFilters } from '../../application/entities/analysis-list-filters.entity';
import { AnalysisListResult } from '../../application/entities/analysis-list-result.entity';
import { AnalysisFilterRequestDto, StandardFilterValueDto } from '../dto/analysis-filter-request.dto';
import { AnalysisListItemDto, AnalysisListResponseDto } from '../dto/analysis-list-response.dto';
import { AnalysisStatsResponseDto } from '../dto/analysis-stats-response.dto';

export const buildFilterBody = (filters: AnalysisListFilters): AnalysisFilterRequestDto => {
  const values: StandardFilterValueDto[] = [];

  if (filters.status) {
    values.push({ property: 'status', propType: 'STRING', propValue: filters.status });
  }
  if (filters.searchQuery) {
    values.push({
      filter: {
        operation: 'OR',
        values: [
          { property: 'title', propType: 'STRING', propValue: filters.searchQuery },
          { property: 'model', propType: 'STRING', propValue: filters.searchQuery },
          { property: 'uuid', propType: 'STRING', propValue: filters.searchQuery },
        ],
      },
    });
  }
  if (filters.locationUuid) {
    values.push({ property: 'locationUuid', propType: 'STRING', propValue: filters.locationUuid });
  }
  if (filters.locationName) {
    values.push({ property: 'locationName', propType: 'STRING', propValue: filters.locationName });
  }
  if (filters.consultantName) {
    values.push({ property: 'consultantName', propType: 'STRING', propValue: filters.consultantName });
  }
  if (filters.riskLevel) {
    values.push({ property: 'riskLevel', propType: 'STRING', propValue: filters.riskLevel });
  }
  if (filters.client) {
    values.push({ property: 'client', propType: 'STRING', propValue: filters.client });
  }

  return values.length > 0
    ? { filter: { operation: 'AND', values } }
    : {};
};

export const hasActiveFilters = (filters?: AnalysisListFilters): boolean => {
  if (!filters) return false;
  return !!(
    filters.status ||
    filters.searchQuery ||
    filters.locationUuid ||
    filters.locationName ||
    filters.consultantName ||
    filters.riskLevel ||
    filters.client
  );
};

const toProgressPercent = (completed: number, total: number): number =>
  total > 0 ? Math.round((completed / total) * 100) : 0;

const toListItem = (dto: AnalysisListItemDto) => ({
  id: dto.uuid,
  title: dto.title,
  description: dto.description,
  currentPl: dto.currentPl ?? 'e',
  requiredPl: dto.requiredPl ?? 'e',
  riskLevel: (dto.riskLevel ?? null) as RiskLevel | null,
  averageHrn: dto.averageHrn ?? null,
  status: dto.status as AnalysisStatus,
  tasksCompleted: dto.tasksCompleted,
  tasksTotal: dto.tasksTotal,
  progressPercent: toProgressPercent(dto.tasksCompleted, dto.tasksTotal),
  locationPath: dto.locationPath.map(l => ({ levelName: l.levelName, locationName: l.locationName })),
  consultant: dto.consultant ?? '—',
  client: dto.client,
  updatedAt: dto.updatedAt,
  imageUrl: dto.imageUrl ?? '',
});

export const toAnalysisListResult = (
  listDto: AnalysisListResponseDto,
  statsDto: AnalysisStatsResponseDto,
  filters?: AnalysisListFilters,
): AnalysisListResult => {
  const paging = listDto.metadata.paging;
  const limit = filters?.limit ?? paging.limit ?? 10;
  const page = filters?.page ?? 1;

  return {
    items: listDto.data.analyses.map(toListItem),
    paging: {
      init: (page - 1) * limit,
      limit,
      total: paging.total ?? 0,
      totalPages: paging.totalPages ?? 0,
    },
    stats: {
      total: statsDto.data.total,
      inProgress: statsDto.data.inProgress,
      requiresAction: statsDto.data.requiresAction,
      overdue: statsDto.data.overdue,
      closed: statsDto.data.closed,
      // El backend siempre los entrega; `?? 0` solo protege la pantalla ante un error o un backend
      // antiguo (no se recalcula a partir de los análisis cerrados).
      totalRequirements: statsDto.data.totalRequirements ?? 0,
      completedRequirements: statsDto.data.completedRequirements ?? 0,
      compliance: statsDto.data.compliance ?? 0,
    },
  };
};
